import React, { useEffect, useState } from 'react';

import { GoogleMap, GoogleMapProps } from '@react-google-maps/api';
import { useTranslation } from 'react-i18next';

import { FeatureFlag } from 'constants-flags';
import { useFlag } from 'lane-shared/hooks';

import styles from './Map.scss';

declare global {
  interface Window {
    google: any;
  }
}

const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-script';

type Props = GoogleMapProps & {
  children?: React.ReactNode;
  byRef?: ((ref: any) => void) | React.MutableRefObject<any>;
};

function MapComponent({ children, byRef, ...otherProps }: Props) {
  const { t } = useTranslation();
  const isMapsModuleFixEnabled = useFlag(FeatureFlag.MapsModuleFixes, false);
  // because google maps script is async we may have it before or after react initialization
  const [isLoading, setIsLoading] = useState(!window.google);
  const [isLoadingError, setIsLoadingError] = useState(false);

  useEffect(() => {
    const googleMapsScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID)!;
    const onGoogleMapLoaded = function () {
      setIsLoading(false);
      setIsLoadingError(!window.google);
    };

    // this listener waits for google map script to load if it haven't been loaded already by that point
    if (!window.google && googleMapsScript) {
      googleMapsScript.addEventListener('load', onGoogleMapLoaded);
    }

    return () => {
      if (googleMapsScript) {
        googleMapsScript.removeEventListener('load', onGoogleMapLoaded);
      }
    };
  }, []);

  if (isLoading) {
    return <div>{t('Map is loading')}</div>;
  }

  if (isLoadingError) {
    return <div>{t('No maps available')}</div>;
  }

  if (isMapsModuleFixEnabled) {
    const { onLoad: onLoadProp, ...mapProps } = otherProps as GoogleMapProps & { onLoad?: (map: google.maps.Map) => void };
    return (
      <div className={styles.container}>
        <GoogleMap
          mapContainerClassName={styles.map}
          onLoad={(map) => {
            if (typeof byRef === 'function') {
              byRef(map);
            } else if (byRef) {
              byRef.current = map;
            }
            onLoadProp?.(map);
          }}
          {...mapProps}
        >
          {children}
        </GoogleMap>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.map}>
        <GoogleMap ref={byRef} {...otherProps}>
          {children}
        </GoogleMap>
      </div>
    </div>
  );
}

export default MapComponent;
